import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { offerApi } from '../apis/offerApis';
import { ACQUISITION_TYPE_CODES, SEASONALITY_PERIODS } from '../constants';
import { RulesTASRelational } from '../utils/tasRelational';

export function useOfferProperties() {
  const {
    data: offerProperties,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['offerProperties'],
    queryFn: async () => {
      const properties = await offerApi.getOfferProperties();
      return properties;
    },
  });

  const acquisitionTypeIdsByCodes = useMemo(() => {
    return offerProperties?.acquisitionTypes.reduce(
      (acc, acq) => {
        acc[acq.code] = acq.id;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [offerProperties]);

  const checkoutTypeIdsByCodes = useMemo(() => {
    return offerProperties?.types.reduce(
      (acc, type) => {
        acc[type.code] = type.id;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [offerProperties]);

  const seasonalityIdsByPeriods = useMemo(() => {
    return offerProperties?.seasonalities.reduce(
      (acc, seasonality) => {
        acc[seasonality.period] = seasonality.id;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [offerProperties]);

  const rulesTASRelational = useMemo<RulesTASRelational[]>(() => {
    if (!acquisitionTypeIdsByCodes || !seasonalityIdsByPeriods) return [];
    return [
      {
        fromType: 'acquisition',
        fromId: acquisitionTypeIdsByCodes[ACQUISITION_TYPE_CODES.purchase],
        toType: 'seasonality',
        toInclude: [seasonalityIdsByPeriods[SEASONALITY_PERIODS.lifetime]],
      },
      {
        fromType: 'acquisition',
        fromId: acquisitionTypeIdsByCodes[ACQUISITION_TYPE_CODES.rent],
        toType: 'seasonality',
        toInclude: [
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.sevenDays],
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.fifteenDays],
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.monthly],
        ],
      },
      {
        fromType: 'acquisition',
        fromId: acquisitionTypeIdsByCodes[ACQUISITION_TYPE_CODES.subscription],
        toType: 'seasonality',
        toExclude: [
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.sevenDays],
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.fifteenDays],
          seasonalityIdsByPeriods[SEASONALITY_PERIODS.lifetime],
        ],
      },
    ];
  }, [acquisitionTypeIdsByCodes, seasonalityIdsByPeriods]);

  return {
    offerProperties,
    acquisitionTypeIdsByCodes,
    checkoutTypeIdsByCodes,
    seasonalityIdsByPeriods,
    rulesTASRelational,
    isLoading,
    isError,
  };
}
